import React, { ChangeEventHandler, useRef, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { isAxiosError } from 'axios';
import Modal from '../../../../components/Modal';
import { TanstackUncontrolledTable } from '../../../../components/TanstackTable';
import { SettlementItem } from '../../types/columns';
import { LIST_PAGINATION_MODEL } from '../../../../store/modules/app/models';
import { StyledMemo, StyledNote, StyledTextarea, StyledWrapper, WarningTextStyle } from './styles';
import { MAXIMUM_NUMBER_OF_BALANCE_ACCOUNT_MEMO_TEXT } from '../../../../constants/service/app/balanceConstants';
import { useBulkUpdateSettlementItems } from '../../hooks';
import { convertStatus } from '../../utils';
import toastMsg from '../../../../lib/toastMsg';
import columns from '../SettlementList/columns';
import { asyncSimpleModal } from '../../../../lib/common';
import Button from '../../../../components/Button';
import { TanstackUncontrolledTableTableRef } from '../../../../components/TanstackTable/types';

const INITIAL_PAGE = LIST_PAGINATION_MODEL.pageNum + 1;
const INITIAL_PAGE_SIZE = LIST_PAGINATION_MODEL.pageSize;

type Props = {
  open: boolean;
  onCancel: () => void;
  bulkItems: SettlementItem[];
  type: '대기' | '보류' | '완료';
};

const SettlementBulkModal = ({ open, onCancel, bulkItems, type }: Props): JSX.Element => {
  const [memo, setMemo] = useState('');
  /**
   * 완료 처리 시 함께 기록할 정산 완료일 (YYYY-MM-DD).
   * default 는 오늘(dayjs()). 미래 날짜는 disabledDate 로 차단 — 회계 일자라 의미 없음.
   * type !== '완료' 인 경우 request 에 미포함.
   */
  const [completeDate, setCompleteDate] = useState<Dayjs>(dayjs());

  const [sorting, setSorting] = useState<SortingState>([{ id: 'id', desc: true }]);
  const [filtering, setFiltering] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: INITIAL_PAGE - 1,
    pageSize: INITIAL_PAGE_SIZE,
  });

  const [hasError, setHasError] = useState(false);

  const bulkEditableItems = bulkItems.filter(({ status }) => ['대기', '보류'].includes(status));

  // 완료 처리시에만 completeDate 포함. 다른 status 전이에서는 서버에서 무시되지만
  // 보내지 않는 편이 의도가 명확하므로 type 별 분기로 처리.
  const request = bulkEditableItems.map((item) => ({
    id: item.id,
    status: convertStatus(type),
    memo,
    completeDate: type === '완료' ? completeDate.format('YYYY-MM-DD') : undefined,
  }));

  const { mutateAsync } = useBulkUpdateSettlementItems(request);

  const handleChangeMemo: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setHasError(false);

    if (e.target.value.length <= MAXIMUM_NUMBER_OF_BALANCE_ACCOUNT_MEMO_TEXT) {
      setMemo(e.target.value);
    }
  };

  const onOk = async (): Promise<void> => {
    try {
      await mutateAsync();

      toastMsg.success('정산 일괄 처리를 성공했습니다.');
      onCancel();
    } catch (error) {
      if (isAxiosError(error)) {
        toastMsg.error(`정산 일괄 처리를 실패했습니다.: ${error.message}`);
      }
    }
  };

  const showConfirm = async (): Promise<void> => {
    const result = await asyncSimpleModal({
      type: 'confirm',
      title: `정산 일괄 ${type} 처리`,
      message: `정산 일괄 ${type} 처리를 진행하시겠습니까?`,
      okText: '확인',
      cancelText: '아니오',
      centered: true,
    });

    if (result) {
      await onOk();
    }
  };

  const tableRef = useRef<TanstackUncontrolledTableTableRef>(null);

  const handleResetFilters = (): void => {
    tableRef.current?.resetFilters();
    tableRef.current?.resetSorting();
  };

  return (
    <Modal
      title={`정산 일괄 ${type} 처리`}
      isOpen={open}
      width={1000}
      height={600}
      isFooter={true}
      onOk={(): void => {
        if (!memo.trim()) {
          setHasError(true);
          return;
        }

        showConfirm();
      }}
      onClose={onCancel}
    >
      <StyledWrapper>
        <div className="bulk-edit-balance-modal-wrap">
          <div className="bulk-edit-balance-count-wrap">
            <div className="bulk-edit-balance-count">
              <div>요청: {bulkItems.length}</div>
              <div className="valid">가능: {bulkEditableItems.length}</div>
              <div className="invalid">불가능: {bulkItems.length - bulkEditableItems.length}</div>
            </div>
            <Button text="필터 초기화" color="primary" onClick={handleResetFilters} />
          </div>

          {type === '완료' && (
            <div className="bulk-edit-balance-complete-date">
              <StyledMemo>
                <StyledNote>*</StyledNote> 완료일
              </StyledMemo>
              <DatePicker
                value={completeDate}
                onChange={(d) => {
                  // styled(DatePicker) 가 아니라 antd 의 DatePicker 직접 사용이지만,
                  // null 케이스(allowClear=false 라 발생 안 함) 도 방어적으로 처리.
                  if (dayjs.isDayjs(d)) setCompleteDate(d);
                }}
                allowClear={false}
                format="YYYY-MM-DD"
                // 완료일은 오늘까지만 허용 — 미래 날짜는 회계 일자로서 의미 없음.
                disabledDate={(d: Dayjs) => d.isAfter(dayjs(), 'day')}
              />
            </div>
          )}

          <div className="bulk-edit-balance-memo">
            <div>
              <StyledMemo>
                <StyledNote>*</StyledNote> 메모 ({memo.length} / {MAXIMUM_NUMBER_OF_BALANCE_ACCOUNT_MEMO_TEXT})
              </StyledMemo>
              <div>
                <StyledTextarea value={memo} onChange={handleChangeMemo} />
                <WarningTextStyle>{hasError && '메모를 입력해주세요'}</WarningTextStyle>
              </div>
            </div>
          </div>

          <div className="bulk-edit-balance-list">
            <TanstackUncontrolledTable
              ref={tableRef}
              emptyText="요청하신 검색 필터로 검색된 정산 리스트가 없습니다."
              data={bulkItems}
              rowCount={bulkItems.length}
              columns={columns}
              sortingState={[sorting, setSorting]}
              filteringState={[filtering, setFiltering]}
              paginationState={[pagination, setPagination]}
              columnPinning={[]}
            />
          </div>
        </div>
      </StyledWrapper>
    </Modal>
  );
};

export default SettlementBulkModal;
